import { ApiGetCompaniesResponse } from '../../../module/api/api/isp-web/companies/api-get-companies.response';
import {
  ApiGetCompanyMembersResponse,
} from '../../../module/api/api/company-web/members/api-get-company-members.response';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiCompany, apiIsp, ApiManager } from '../../../managers/api-manager';
import { breadcrumbParts } from '../../../models/breadcrumb-parts';
import { CompanyData } from '../../../models/company-data';
import { PageComponentDefaultProps } from '../../../models/page-component-default-props';
import { BreadcrumbList } from '../../ui/breadcrumb-list/breadcrumb-list';
import { Button } from '../../ui/button/button';
import { Checkbox } from '../../ui/checkbox/checkbox';
import { DatePicker } from '../../ui/date-picker/date-picker';
import { Header } from '../../ui/header/header';
import { LabeledForm } from '../../ui/input/labeled-form';
import { Select } from '../../ui/select/select';
import { SideBar } from '../../ui/sidebar/sidebar';
import { ReactSelect, SelectInfoData, ChangeLabelData } from '../../ui/select/react-select';
import { useDidMount } from '../../../hooks/life-cycle';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { CompanyUserDetail } from '../../../models/company-user-data';
import { Radiobutton } from '../../ui/radiobutton/radiobutton';
import './operation-log.scss';
import { cloneDeep } from 'lodash';
import { dialogAction } from '../../../slices/dialog-slice';
import { MultiValue, SingleValue } from 'react-select';
import { Input } from '../../ui/input/input';
import { DateFormatter, ZeroPadding } from '../../../utilities';
import { ValidationFactory } from '../../../managers/validation/validation-factory';
import { CheckAllValid, Validation } from '../../../managers/validation/validation';
import deleteIcon from '../../../assets/images/common/icon_delete.svg';
import { useAutoSelectLogOperation } from '../../../hooks/use-async-log-operation';
import { useLogProduct } from './hooks/use-log-product';
import { push } from 'connected-react-router';
import { RoutingPath } from '../../../routes/routing-path';
import { ApiGetIspUsersResponse } from '../../../module/api/api/isp-web/users/api-get-isp-users.response';
import { IspUserBody, IspUserData } from '../../../models/isp-user-data';
import { IspLogOperation } from '../../../models/logs/isp-log-operation';

export type SelectData = {
  key: string,
  label: string,
};

const ispBreadcrumbList = [
  breadcrumbParts.isp.home,
  breadcrumbParts.isp.ispAdminLog,
];

const companyBreadcrumbList = [
  breadcrumbParts.company.home,
  breadcrumbParts.company.ispLog,
];

// バリデーション用意
const validations: { [key: string]: Validation } = {
  checkAfterTodayStartDate: ValidationFactory('require'),
  checkAfterTodayEndDate: ValidationFactory('require'),
  checkStartThanPastDate: ValidationFactory('require'),
};

export const AdminOperationLog = (props: PageComponentDefaultProps) => {
  const { apiManger } = props;
  const breadcrumbList = apiManger.type === 'isp' ? ispBreadcrumbList : companyBreadcrumbList;
  const { companyUserInfo } = useAppSelector((state) => state.loginCompanyUserInfo);

  /* Hooks */
  const dispatch = useAppDispatch();
  /* State */
  /* ---- 検索 ---- */
  /* システム選択 */
  const [isAdminSite, setIsAdminSite] = useState(true);
  /* プロダクト選択 */
  /* 選択企業　*/
  const [selectedCompany, setSelectedCompany] = useState<SingleValue<SelectInfoData> | undefined>(undefined);
  const company = useMemo(() => {
    return selectedCompany?.value ?? '';
  }, [selectedCompany]);
  /* プロダクトリスト関連 */
  const { requestLogProduct, productList, productCheckToggle, initProduct } = useLogProduct({ companyCode: apiManger.type === 'isp' ? company : companyUserInfo.company_code, isPortal: !isAdminSite });
  /* 選択ユーザー */
  const [userMailBody, setUserMailBody] = useState<SingleValue<SelectInfoData> | undefined>(undefined);
  /* 日付　*/
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  /* ログ開始日 */
  const [_startLogDate, setStartLogDate] = useState<Date | null>(null);

  const startLogDate = useMemo(() => {
    if (apiManger.type === 'company') return _startLogDate;
    return null;
  }, [_startLogDate, apiManger]);
  /* -------------- */

  /* 各種リスト */
  const [companyInfoList, setCompanyInfoList] = useState<CompanyData[]>([]);
  const [userList, setUserList] = useState<IspUserData[]>([]);
  /* DLボタン制御 */
  const [validFlag, setValidFlag] = useState(false);
  // バリデーションのトータルチェック
  const [checkValidation, setCheckValidation] = useState(CheckAllValid(validations));
  /* Memo */
  /* Ref */
  const startRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLInputElement>(null);
  /* - 企業プルダウンリスト - */
  const viewList = useMemo<SelectInfoData[]>(() => {
    return companyInfoList.map((v, i) => ({ value: v.company_code, label: v.company_name }));
  }, [companyInfoList]);
  /* - ユーザーリスト - */
  const mailList = useMemo<SelectInfoData[]>(() => {
    return userList.map((v, i) => ({ value: v.user_id, label: v.email }));
  }, [userList]);
  /* - ユーザーリスト メールから名前に変更用の値 - */
  const userInfoList = useMemo<ChangeLabelData[]>(() => {
    return userList.map((v, i) => ({ value: v.user_id, label: v.email, name: `${v.family_name} ${v.name}` }));
  }, [userList]);

  const changeDate = useMemo<string>(() => {
    if (!endDate || !_startLogDate) return DateFormatter.date2str(new Date(), 'YYYYMMDD', '-');
    const cloneDate = cloneDeep(endDate);
    cloneDate.setFullYear(cloneDate.getFullYear() - 3);
    const result = cloneDate < _startLogDate ? _startLogDate : cloneDate;

    return DateFormatter.date2str(result, 'YYYYMMDD', '-');
  }, [endDate]);

  // バリデーション用登録時の日付
  const now = useMemo(() => {
    return new Date();
  }, []);
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const day = now.getDate();

  const todayValid = useMemo(() => {
    return String(year) + ZeroPadding(month, 2) + ZeroPadding(day, 2);
  }, [year, month, day]);

  // - ソートキーの命名 -
  const sortKey = {
    code: 1,
    name: 2,
    storage: 3,
    createdAt: 4,
    stopAt: 5,
  };
  // - 昇降の明示 -
  const highlowKey = {
    high: 0,
    low: 1,
  };

  // - ユーザー選択 -
  const changeUser = useCallback((v) => {
    setUserMailBody(v);
  }, [mailList]);

  // - DLボタン -
  const onClickDL = useCallback(() => {
    if (apiManger.type === 'isp') {
    const _user = userList.find((v) => v.user_id === userMailBody?.value);
      IspLogOperation('adminLogManagerLogDL');
      apiIsp.logs().operations().admin().download().getAdmin({
        extract_date_start: startDate ? DateFormatter.date2str(new Date(startDate), 'YYYYMMDD', '-') : '',
        extract_date_end: endDate ? DateFormatter.date2str(new Date(endDate), 'YYYYMMDD', '-') : DateFormatter.date2str(new Date(), 'YYYYMMDD', '-'),
        user_id: _user?.user_id ?? '',
      }).then((res) => {
        const a = document.createElement('a');
        const url = URL.createObjectURL(res);
        function formatDate(date = new Date()) {
          const pad = (n: number) => n.toString().padStart(2, '0');
        
          return (
            date.getFullYear().toString() +
            pad(date.getMonth() + 1) +
            pad(date.getDate()) +
            pad(date.getHours()) +
            pad(date.getMinutes()) +
            pad(date.getSeconds())
          );
        }        
        a.download = `ISPユーザー操作ログ.csv`;
        a.href = url;
        a.click();
        URL.revokeObjectURL(url);
        a.remove();
      }).catch((e) => {
        ApiManager.errorFunc(e);
      });
    } else {
      return;
    }
  }, [isAdminSite, viewList, mailList, userMailBody, startDate, endDate, apiManger, requestLogProduct]);

  const sortInitialize = useCallback(() => {
    initProduct();
    setIsAdminSite(true);
    if (apiManger.type !== 'company') {
      setSelectedCompany(undefined);
      // setUserList([]);
      setStartLogDate(null);
    }
    setUserMailBody(undefined);
    setStartDate(null);
    setEndDate(null);
  }, [apiManger.type]);

  /* 検索条件削除 */
  const onClickSortDelete = useCallback(() => {
    sortInitialize();
  }, [sortInitialize]);

  // - 指定開始日 -
  const changeStartDate = useCallback((v) => {
    setStartDate(v);
    window.setTimeout(() => {
      if (endRef.current) {
        endRef.current.focus();
        endRef.current.blur();
      }
    }, 0)
  }, [endRef]);
  // - 指定終了日 -
  const changeEndDate = useCallback((v) => {
    setEndDate(v);
    window.setTimeout(() => {
      if (startRef.current) {
        startRef.current.focus();
        startRef.current.blur();
      }
    }, 0)
  }, [startRef]);

  /* Effect */
  // - 企業選択 一覧取得 -
  useDidMount(() => {
    if (apiManger.type === 'company') {
      dispatch(push(RoutingPath.companyStatus));
      return;
    }
    apiIsp.users().get({ limit: 99999 }).then((e: any) => {
      const res: ApiGetIspUsersResponse = e;
      setUserList(res.body.data ?? []);
    })
    
    validations['checkAfterTodayStartDate'] = new Validation({
      test: (v: string) =>
        v === '' || Number(v.replace(/-/g, '')) <= Number(todayValid),
      errorMessages: ['今日以降の設定はできません'],
    });
    validations['checkAfterTodayEndDate'] = new Validation({
      test: (v: string) =>
        v === '' || Number(v.replace(/-/g, '')) <= Number(todayValid),
      errorMessages: ['今日以降の設定はできません'],
    });
    setValidFlag(true);
  });

  useEffect(() => {
    if (!startRef.current || !endRef.current) return;
    validations['checkStartThanPastDate'] = new Validation({
      test: (v: string) =>
        v === '' || Number(startRef.current!.value.replace(/-/g, '')) <= Number(v.replace(/-/g, '')),
      errorMessages: ['指定開始日より過去日の設定はできません'],
    });
  }, [validFlag]);

  useEffect(() => {
    setCheckValidation(CheckAllValid(validations));
  }, [startDate, endDate, company, userMailBody, selectedCompany]);
  const isDisabled = useMemo(() => {
    return !!(
      (!startDate && !endDate)
      || (startDate && endDate && endDate.getTime() < startDate.getTime()));
  }, [
    startDate,
    endDate,
  ]);
  return (
    <div
      id="App"
      className="operation_log"
    >
      <SideBar
        currentPage="admin-log-Management"
        apiManger={apiManger.type}
      />
      <div className="main_cnt">
        <Header apiManger={apiManger.type} />
        <div className="inner">
          <BreadcrumbList breadcrumbList={breadcrumbList} />
          <div className="inner">
            <section className="">
              <header>
                <h2>ISPユーザー操作ログ</h2>
              </header>
              <div className="operation_log__body__wrap edit_wrap">
                <div className="operation_log__body">
                  <div className="operation_log__body__row row_forms">
                    <div className="operation_log__body__row">
                    <LabeledForm
                      label="ISPユーザー名"
                      formEle={<ReactSelect
                        list={mailList}
                        select={userMailBody}
                        changeLabelList={userInfoList}
                        handleChange={changeUser}
                        placeholder="ユーザーメールアドレス"
                        noOptionsMessage={'該当するメールアドレスが存在しません。'}
                        isClearable
                        formMode={'pulldown'}
                      />}
                      className="user_name"
                    />
                  </div>
                    <Button
                      className="delete_btn"
                      label=""
                      onClick={onClickSortDelete}
                      color="quaternary"
                      icon={<img src={deleteIcon} alt="" />}
                      size="large"
                      title="入力内容をリセット"
                    />
                  </div>
                  <div className="operation_log__body__row row_period edit_wrap">
                    <div className="period_form__wrap date_box">
                      <div className="period_form">
                        <LabeledForm
                          label="指定開始日"
                          formEle={
                            <Input
                              type="date"
                              onChange={(e) => {
                                changeStartDate(e.target.value ? new Date(e.target.value) : null)
                              }}
                              onClickClearDate={() => changeStartDate(null)}
                              value={startDate ? DateFormatter.date2str(startDate, 'YYYYMMDD', '-') : ''}
                              max={DateFormatter.date2str(new Date(), 'YYYYMMDD', '-')}
                              min={startLogDate ? DateFormatter.date2str(startLogDate, 'YYYYMMDD', '-') : undefined}
                              validations={[validations.checkAfterTodayStartDate]}
                              ref={startRef}
                            />
                          }
                        />
                        <div className="period_form__tilde" />
                        <LabeledForm
                          label="指定終了日"
                          formEle={
                            <Input
                              type="date"
                              onChange={(e) => {
                                changeEndDate(e.target.value ? new Date(e.target.value) : null)
                              }}
                              value={endDate ? DateFormatter.date2str(endDate, 'YYYYMMDD', '-') : ''}
                              onClickClearDate={() => changeEndDate(null)}
                              max={DateFormatter.date2str(new Date(), 'YYYYMMDD', '-')}
                              min={startLogDate ? DateFormatter.date2str(startLogDate, 'YYYYMMDD', '-') : undefined}
                              validations={[validations.checkAfterTodayEndDate, validations.checkStartThanPastDate]}
                              ref={endRef}
                            />
                          }
                        />
                      </div>
                      <div
                        className="period_form__announce"
                        style={{ opacity: apiManger.type === 'isp' && !company ? 0.5 : 1 }}>
                        ※開始日または終了日のどちらか一方は必ず設定してください
                          </div>
                    </div>
                    <Button
                      label="DL"
                      onClick={onClickDL}
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};